import { call, all, takeLeading, takeLatest, put, select, takeEvery, spawn, join, race, take, delay } from 'redux-saga/effects';
import tokenService from 'services/tokenService';
import { api, apiCall } from 'redux/helpers/api';
import { userActions } from 'redux/user';
import NotificationService from 'services/notificationService';
import { snackbarActions } from 'redux/snackbar';
import history from 'providers/history';
import appDownloadPrompt from 'services/appDownloadPrompt';
import { onAppInit } from 'services/authService';
import { isFailedToFetchError } from 'redux/helpers/utils';
import { takeOnce, debouncePerType, cancelledBy } from 'redux/helpers/saga';
import pulseTypes from 'redux/pulse/types';
import pageTypes from 'redux/page/types';
import folderTypes from 'redux/documentFolder/types';
import feedbackTypes from 'redux/feedback/types';
import types from './types';
import signedOutTypes from './signedOutTypes';
import actions from './actions';
import selectors, { getUserIsLoggedIn, getWorkspace } from './selectors';

function* storeMember(workspace, { memberId, memberStatus, permissions, features, integrations }) {
  yield call(tokenService.setWorkspaceId, workspace.id);
  yield put(actions.getMemberSuccess({ workspaceId: workspace.id, memberId, memberStatus, permissions, features, integrations }));
}

function* requestMember(workspace) {
  const { ok, response, error } = yield apiCall(api.workspace.getMe, { workspaceId: workspace.id });

  if (ok) {
    yield call(storeMember, workspace, response);
  }

  return { ok, error, member: response };
}

const MEMBER_LOAD_TIMEOUT = 30 * 1000;

// One load of the member. It's dropped if the user signs out first, so it can't land in the next session, and given up
// after a while, so a request that never answers can't hold up the loads queued behind it
function* runMemberLoad(workspace) {
  const { loaded, signedOut } = yield race({
    loaded: call(requestMember, workspace),
    signedOut: take(signedOutTypes),
    timedOut: delay(MEMBER_LOAD_TIMEOUT),
  });

  if (loaded) {
    return loaded;
  }

  return { ok: false, error: signedOut ? { type: 'SignedOut' } : new TypeError('Failed to fetch') };
}

let memberLoad = null;

// The only way the member is requested. Loads run one at a time, each starting once the previous has answered,
// so answers can't arrive out of order and a load asked for after a change sees that change
function* loadMember(workspace) {
  if (!workspace?.id) {
    return { ok: true };
  }

  while (memberLoad?.isRunning()) {
    const previous = yield join(memberLoad);

    // The user signed out while this one waited, so it has no session to load for
    if (previous?.error?.type === 'SignedOut') {
      return previous;
    }
  }

  // Detached from its caller, so cancelling one caller can't cancel a load that another one waits for
  memberLoad = yield spawn(runMemberLoad, workspace);

  return yield join(memberLoad);
}

const featureLimitCountChangeTypes = [
  pulseTypes.storePulseSurveySuccess,
  pulseTypes.deletePulseSurveySuccess,
  pageTypes.storeSuccess,
  folderTypes.postFolderForAdminSuccess,
  folderTypes.deleteFolderVariantForAdminSuccess,
  feedbackTypes.storeFeedbackTypeSuccess,
  feedbackTypes.deleteFeedbackTypeSuccess,
];

// Initial load's (Refresh) and login's response is the same and actions are same.
function* handleAuthResponse({ ok, response, error }) {
  if (ok) {
    const { me, token, workspaces, intercom } = response;

    const accessToken = token;
    const refreshToken = token;

    yield call(tokenService.setAuthTokens, accessToken, refreshToken);

    const member = yield call(loadMember, workspaces[0]);
    if (!member.ok) {
      return yield call(handleAuthFailure, member.error);
    }

    // For now trigger manually.
    // Later add checks if login, register etc don't trigger it themselves, then fetch from server
    yield put(actions.getAuthUserSuccess({ user: me, workspace: workspaces[0], intercom }));
    yield put(actions.loginSuccess());

    yield put(userActions.getNotifications());
    yield call(NotificationService.firebase.askPermission);

    // After the permission prompt, so the badge count can be shown on the app icon
    yield put(userActions.getBadges());

    return { ok, error };
  }

  return yield call(handleAuthFailure, error);
}

function* handleAuthFailure(error) {
  if (!isFailedToFetchError(error)) {
    yield call(tokenService.clearSession);
    yield put(actions.loginFailure(error));
  }

  return { ok: false, error };
}

function* onInitAuth({ payload }) {
  yield call(onAppInit, payload);
}

function* onLogin({ payload }) {

  const fcmToken = yield call(tokenService.getFcmToken);
  const apnsToken = yield call(tokenService.getApnsToken);

  const data = yield apiCall(api.auth.login, {
    ...(fcmToken && { fcmToken }),
    ...(apnsToken && { apnsToken }),
    ...payload,
  });

  const { ok, error } = yield handleAuthResponse(data);

  if (ok) {
    yield put(snackbarActions.createSuccess('login.success'));
  } else if (isFailedToFetchError(error)) {
    yield put(snackbarActions.createFailure('login.failedToFetch', { translate: true }));
  } else {
    yield put(snackbarActions.createFailure('login.failure', { translate: true }));
  }
}

function* onRegister({ payload, config = {} }) {

  const fcmToken = yield call(tokenService.getFcmToken);
  const apnsToken = yield call(tokenService.getApnsToken);

  const data = yield apiCall(api.auth.register, {
    ...(fcmToken && { fcmToken }),
    ...(apnsToken && { apnsToken }),
    ...payload,
  }, config);

  // The account exists once data is ok, but signing in can still fail on loading the member
  const { ok, error } = data.ok ? yield call(handleAuthResponse, data) : data;

  if (ok) {
    // Setting the new workspace up is for whoever may change it, which its creator normally can
    const canSetUp = yield select(state => selectors.hasPermissionSelector(state)('ADMIN_SETTINGS'));

    yield call(appDownloadPrompt.show);
    yield call(history.push, canSetUp ? '/setup' : '/');
    yield put(snackbarActions.createSuccess('auth.register'));
  } else {
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onJoin({ payload, config = {} }) {

  const fcmToken = yield call(tokenService.getFcmToken);
  const apnsToken = yield call(tokenService.getApnsToken);

  const data = yield apiCall(api.auth.join, {
    ...(fcmToken && { fcmToken }),
    ...(apnsToken && { apnsToken }),
    ...payload,
  }, config);

  // The account exists once data is ok, but signing in can still fail on loading the member
  const { ok, error } = data.ok ? yield call(handleAuthResponse, data) : data;

  if (ok) {
    yield call(appDownloadPrompt.show);
    yield call(history.push, '/');
    yield put(snackbarActions.createSuccess('auth.register'));
  } else {
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onLogout() {
  const fcmToken = yield call(tokenService.getFcmToken);
  const apnsToken = yield call(tokenService.getApnsToken);

  const { error, ok } = yield apiCall(api.auth.logout, {
    ...(fcmToken && { fcmToken }),
    ...(apnsToken && { apnsToken }),
  });

  // Regardless of what happens (maybe server unreachable), clear the local token
  yield call(tokenService.clearSession);

  if (ok || error.message === 'Unauthenticated.' || error.type === 'AuthenticationException') {
    yield put(actions.logoutSuccess());
  } else {
    yield put(actions.logoutFailure(error.message));
  }
}

function* onGetAuthUser() {
  const rememberedWorkspaceId = yield call(tokenService.getWorkspaceId);

  // Guess the member from the last workspace so boot doesn't wait for auth/user; a miss is loaded again below.
  // Storage holds the id as a string, the store as a number
  const remembered = rememberedWorkspaceId ? { id: Number(rememberedWorkspaceId) } : null;
  const [authUser, rememberedMember] = yield all([
    apiCall(api.auth.getAuthUser),
    remembered ? call(loadMember, remembered) : null,
  ]);

  if (!authUser.ok) {
    yield call(onGetAuthUserFailure, authUser.error);
    return;
  }

  const { me, workspaces, intercom } = authUser.response;

  if (!(rememberedMember?.ok && workspaces[0]?.id === remembered.id)) {
    const member = yield call(loadMember, workspaces[0]);

    if (!member.ok) {
      yield call(onGetAuthUserFailure, member.error);
      return;
    }
  }

  yield put(actions.getAuthUserSuccess({ user: me, workspace: workspaces[0], intercom }));
  yield put(userActions.getBadges());
}

// Reloads the member of the current workspace while the app keeps showing the one it has
function* onGetMember() {
  const workspace = yield select(getWorkspace);
  const { ok, error, member } = yield call(loadMember, workspace);

  // A new membership in the same workspace, as after a removal and a new invite, comes with auth/user's new member id
  if (ok && member && member.memberId !== workspace.memberId) {
    yield put(actions.getAuthUser());
  }

  if (!ok) {
    yield put(actions.getMemberFailure(error));

    // The membership is gone, so auth/user says which one the user still has, if any
    if (error.type === 'AuthorizationException') {
      yield put(actions.getAuthUser());
    }
  }
}

function* onGetAuthUserFailure(error) {
  yield put(actions.getAuthUserFailure(error));

  if (error.type === 'AuthorizationException') {
    yield put(actions.logout());
  }
}

function* onGetZendeskToken() {
  const { ok, response, error } = yield apiCall(api.auth.getZendeskToken);

  if (ok) {
    yield put(actions.getZendeskTokenSuccess(response));
  } else {
    yield put(actions.getZendeskTokenFailure(error));
  }
}

function* onSendForgotEmail({ payload, config = {} }) {
  const { ok, error } = yield apiCall(api.auth.forgot, payload, config);
  if (ok) {
    yield put(snackbarActions.createSuccess('auth.sendForgotPasswordInstructions'));
    yield call(history.push, '/');
  } else {
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onPasswordReset({ payload, config = {} }) {
  const { ok, error } = yield apiCall(api.auth.reset, payload, config);
  if (ok) {
    yield put(snackbarActions.createSuccess('auth.passwordReset'));
    yield call(history.push, '/login');
  } else {
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onEmailVerification({ payload, config = {} }) {
  const { ok, error } = yield apiCall(api.auth.verifyEmail, payload, config);
  if (ok) {
    yield put(snackbarActions.createSuccess('auth.emailVerification'));
  } else {
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onEmailVerificationValidation({ payload, config = {} }) {
  const { error } = yield apiCall(api.auth.verifyEmailValidate, payload, config);
  if (error) {
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onVerifyToken({ payload, config = {} }) {
  const { error } = yield apiCall(api.auth.verifyToken, payload, config);
  if (error) {
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onGetProfile() {
  const { ok, error, response } = yield apiCall(api.user.getProfile);
  if (ok) {
    yield put(actions.getProfileSuccess(response));
  } else {
    yield put(actions.getProfileFailure(error));
    yield put(snackbarActions.createFailure(error.message));
  }
}

function* onSyncDeviceTokens() {
  const isLoggedIn = yield select(getUserIsLoggedIn);
  if (!isLoggedIn) {
    return;
  }

  const fcmToken = yield call(tokenService.getFcmToken);
  const apnsToken = yield call(tokenService.getApnsToken);

  if (!fcmToken && !apnsToken) {
    return;
  }

  yield apiCall(api.auth.registerDevice, {
    ...(fcmToken && { fcmToken }),
    ...(apnsToken && { apnsToken }),
  });
}

function* onDeleteAccount({ payload }) {
  const fcmToken = yield call(tokenService.getFcmToken);
  const apnsToken = yield call(tokenService.getApnsToken);

  const { error, ok } = yield apiCall(api.auth.deleteAccount, {
    ...(fcmToken && { fcmToken }),
    ...(apnsToken && { apnsToken }),
    ...payload,
  });

  if (ok) {
    yield put(actions.deleteAccountSuccess());
    yield call(tokenService.clearSession);
    yield put(actions.logoutSuccess());
    yield put(snackbarActions.createSuccess('Account deleted!'));
  } else {
    yield put(actions.deleteAccountFailure());
    yield put(snackbarActions.createFailure(error.message));
  }
}

export default function* authSagas() {
  yield all([
    takeLeading(types.login, onLogin),
    takeLeading(types.logout, onLogout),

    takeLeading(types.getAuthUser, cancelledBy(signedOutTypes, onGetAuthUser)),
    // One watcher for every reload, so an older answer can't land after a newer one
    takeLatest([types.getMember, ...featureLimitCountChangeTypes], cancelledBy(signedOutTypes, onGetMember)),

    takeLeading(types.sendForgotEmail, onSendForgotEmail),
    takeLeading(types.resetPassword, onPasswordReset),

    takeLeading(types.verifyToken, onVerifyToken),
    takeLeading(types.verifyEmail, onEmailVerification),
    takeLeading(types.validateEmailVerificationCode, onEmailVerificationValidation),
    takeLeading(types.register, onRegister),
    takeLeading(types.join, onJoin),

    takeLeading(types.getProfile, onGetProfile),

    takeLeading(types.getZendeskToken, onGetZendeskToken),

    takeEvery(types.deleteAccount, onDeleteAccount),

    debouncePerType(500, types.deviceTokenReceived, onSyncDeviceTokens),
    takeEvery(types.loginSuccess, onSyncDeviceTokens),

    // TODO: Is takeOnce suitable for mobiles? Or will it block something important?
    takeOnce(types.initAuth, onInitAuth),
  ]);
}
